import { useState } from 'react'
import { MessageSquarePlus, Trash2 } from 'lucide-react'
import { adminMutate, Select, useToast } from '../../../admin/primitives.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { Field } from '../../../admin/form.js'
import { useConfirm } from '../../../shell/confirm.js'
import { Link } from '../../../router.js'
import { messageEchecMutation, type Lecture } from '../../api.js'
import { BandeauLecture } from '../commun.js'
import { GENRES_ANNOTATION, heureParis, type Annotation, type GenreAnnotation, type ListeAnnotations } from './nuits-model.js'

/**
 * ANNOTATIONS DE LA RÉGIE (`/v1/annotations?night=`, idée 24 de Sonde).
 *
 * Un fait noté par un humain dans la chronologie (« 22:40 balances son de
 * ROOM 1 », « 01:10 strobe 6 débranché volontairement »). Il apparaît dans le
 * rejeu, la fiche d'incident et le rapport du matin. Une note n'est jamais
 * effacée : elle est RETIRÉE (le retrait reste tracé). Droit : sonde.annotate.
 *
 * L'instant noté est celui du curseur du rejeu (ou maintenant, pendant la
 * nuit en cours) : on note ce qu'on regarde.
 */

export interface PropsAnnotations {
  lecture: Lecture<ListeAnnotations> | null
  liste: Annotation[]
  recharger: () => void
  curseur: number | null
  nuitOuverte: boolean
  peutAnnoter: boolean
  libellesZones: Map<string, string>
  onAller: (ms: number) => void
}

export function AnnotationsNuit(p: PropsAnnotations) {
  const toast = useToast()
  const confirm = useConfirm()
  const [genre, setGenre] = useState<GenreAnnotation>('note')
  const [texte, setTexte] = useState('')
  const [zone, setZone] = useState('')
  const [quand, setQuand] = useState<'curseur' | 'maintenant'>('curseur')
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const items = [...p.liste].sort((a, b) => a.at.localeCompare(b.at))
  const lib = (code: string | null) => (code ? p.libellesZones.get(code) ?? code : null)
  const instant = quand === 'maintenant' && p.nuitOuverte ? null : p.curseur
  const possible = texte.trim().length > 0 && (instant !== null || (quand === 'maintenant' && p.nuitOuverte))

  const noter = async () => {
    if (!possible || occupe) return
    setOccupe(true); setErreur(null)
    const corps: Record<string, unknown> = { kind: genre, body: texte.trim(), zone_code: zone || null }
    if (instant !== null) corps['at'] = new Date(instant).toISOString()
    const r = await adminMutate<{ error?: string }>('POST', '/api/v1/supervision/v/annotations', corps, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    })
    setOccupe(false)
    if (r.status >= 200 && r.status < 300) {
      setTexte('')
      toast('ok', `Note ajoutée à ${instant !== null ? heureParis(instant) : 'maintenant'}.`)
      p.recharger()
    } else {
      setErreur(messageEchecMutation(r.status, r.data, 'la note n’a pas été ajoutée'))
    }
  }

  const retirer = async (a: Annotation) => {
    const ok = await confirm({
      title: 'Retirer cette note ?',
      message: 'Elle disparaît du rejeu, de la fiche d’incident et du rapport du matin. Son retrait reste tracé, avec votre nom.',
      details: [{ label: heureParis(a.at), value: a.body.length > 120 ? `${a.body.slice(0, 120)}…` : a.body }],
      variant: 'sensitive',
      confirmLabel: 'Retirer la note',
    })
    if (!ok) return
    const r = await adminMutate<{ error?: string }>('DELETE', `/api/v1/supervision/v/annotations/${encodeURIComponent(a.id)}`, {}, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    })
    if (r.status >= 200 && r.status < 300) { toast('ok', 'Note retirée.'); p.recharger() }
    else toast('error', messageEchecMutation(r.status, r.data, 'la note est restée'))
  }

  const zonesOptions = [{ value: '', label: 'Tout le club' }, ...[...p.libellesZones.entries()].map(([value, label]) => ({ value, label }))]

  return (
    <section className="lc-panel sv-nuit-section" aria-labelledby="sv-nuit-notes-t">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="sv-nuit-notes-t">Notes de la régie</h2>
        <span className="lc-panel__spacer" />
        <span className="lc-hint">{items.length} note{items.length > 1 ? 's' : ''}</span>
      </header>
      <div className="lc-panel__body">
        {p.peutAnnoter ? (
          <form className="sv-nuit-form" onSubmit={(e) => { e.preventDefault(); void noter() }}>
            <Field label="Genre">
              <Select value={genre} options={GENRES_ANNOTATION} onChange={(v) => setGenre(v as GenreAnnotation)} ariaLabel="Genre de la note" />
            </Field>
            <Field label="Salle">
              <Select value={zone} options={zonesOptions} onChange={setZone} ariaLabel="Salle concernée" />
            </Field>
            <div className="sv-nuit-form__large">
              <Field label="Ce qui s’est passé" helper="2 000 caractères au plus. Visible de tous ceux qui voient la supervision.">
                <textarea className="lc-input" value={texte} maxLength={2000} rows={3} onChange={(e) => setTexte(e.target.value)}
                          placeholder="Ex. : balances son de ROOM 1 ; strobe 6 débranché volontairement" aria-label="Texte de la note" />
              </Field>
            </div>
            <div className="sv-nuit-form__pied">
              {p.nuitOuverte ? (
                <SegmentedControl size="sm" value={quand} onChange={setQuand} ariaLabel="Instant noté"
                                  options={[{ value: 'curseur', label: `À ${heureParis(p.curseur)}` }, { value: 'maintenant', label: 'Maintenant' }]} />
              ) : <span className="lc-hint">Notée à {heureParis(p.curseur)}, l’instant du rejeu</span>}
              <button type="submit" className="lc-btn lc-btn--primary" disabled={!possible || occupe}>
                <MessageSquarePlus size={14} aria-hidden="true" /> {occupe ? 'Ajout…' : 'Ajouter la note'}
              </button>
              {erreur ? <span className="lc-form__error" role="alert">{erreur}</span> : null}
            </div>
          </form>
        ) : null}

        <BandeauLecture lecture={p.lecture} />
        {items.length === 0 && p.lecture?.etat === 'ok' ? (
          <p className="sv-nuit-vide">Aucune note pour cette nuit. Une note (balances, intervention, débranchement volontaire…) apparaît dans le rejeu, la fiche d’incident et le rapport du matin.</p>
        ) : null}
        {items.length > 0 ? (
          <ul className="sv-nuit-lignes">
            {items.map((a) => (
              <li key={a.id} className="sv-nuit-l">
                <span className="sv-nuit-l__t">
                  <button type="button" onClick={() => p.onAller(Date.parse(a.at))} title="Rejouer cet instant">{heureParis(a.at)}</button>
                </span>
                <div className="sv-nuit-l__corps">
                  <span className="sv-nuit-l__titre">
                    <span className="sv-nuit-neutre">{a.kind_label}</span>
                    {lib(a.zone_code) ? <span className="sv-nuit-neutre">{lib(a.zone_code)}</span> : null}
                    <span>{a.body}</span>
                  </span>
                  <span className="sv-nuit-l__meta">
                    {a.author.name} · notée le {new Date(a.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })}
                    {a.device ? <> · <Link href={`/infrastructure/appareils/${encodeURIComponent(a.device.uid)}`}>{a.device.display_name}</Link></> : null}
                    {a.incident_id ? <> · <Link href={`/infrastructure/incidents/${encodeURIComponent(a.incident_id)}`}>incident lié</Link></> : null}
                  </span>
                  {p.peutAnnoter ? (
                    <span className="sv-nuit-l__actions">
                      <button type="button" className="lc-btn lc-btn--ghost" onClick={() => void retirer(a)}><Trash2 size={14} aria-hidden="true" /> Retirer</button>
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  )
}
